'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';

const ApertureMark = ({ size = 20, color = '#1F4D3F' }: { size?: number; color?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="1.4"
    strokeLinecap="round"
    width={size}
    height={size}
    aria-hidden="true"
  >
    <g transform="rotate(0 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(60 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(120 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(180 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(240 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
    <g transform="rotate(300 12 12)"><path d="M 12 5 Q 13.2 9 12 11" /></g>
  </svg>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const navLinks = [
    { href: '#intelligence', label: 'Intelligence' },
    { href: '#ask', label: 'Ask vina' },
    { href: '#how-vina-thinks', label: 'How It Works' },
    { href: '#trust', label: 'Trust' },
  ];

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.72)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(212,224,230,0.85)',
        boxShadow: scrolled ? '0 8px 24px rgba(118,147,160,0.1)' : '0 8px 24px rgba(118,147,160,0.06)',
      }}
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left: Brand */}
          <div className="flex items-center gap-3">
            <Link
              href="/"
              aria-label="VINA home"
              className="flex items-center gap-2 transition-all hover:opacity-90"
              style={{
                background: 'rgba(31,77,63,0.06)',
                border: '1px solid rgba(31,77,63,0.12)',
                borderRadius: '13px',
                padding: '7px 14px',
              }}
            >
              <ApertureMark size={18} color="#1F4D3F" />
              <span style={{ fontFamily: 'var(--font-sans)', fontWeight: 500, fontSize: '15px', letterSpacing: '-0.33px', color: '#16352D' }}>
                vina
              </span>
            </Link>
          </div>

          {/* Center Nav — desktop */}
          <nav className="hidden md:flex items-center gap-8" aria-label="Primary navigation">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="text-sm font-medium transition-colors"
                style={{ color: '#4F665F', letterSpacing: '-0.01em' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#1F4D3F')}
                onMouseLeave={e => (e.currentTarget.style.color = '#4F665F')}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right CTA — desktop */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://vasy.ai/india-avail-free-demo?utm_source=lp&utm_medium=web&utm_campaign=teamvina"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium transition-all px-5 py-2 rounded-full"
              style={{ background: '#1F4D3F', color: '#FFFFFF', border: '1px solid rgba(31,77,63,0.2)' }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = '#29584B'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = '#1F4D3F'; }}
            >
              Schedule a Free Demo Now
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2 rounded-lg transition-colors"
            style={{ color: '#4F665F' }}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            suppressHydrationWarning
          >
            <span
              className="block w-5 h-0.5 transition-all duration-300 origin-center"
              style={{
                background: '#4F665F',
                transform: mobileOpen ? 'translateY(6px) rotate(45deg)' : 'none',
              }}
            />
            <span
              className="block w-5 h-0.5 transition-all duration-300"
              style={{
                background: '#4F665F',
                opacity: mobileOpen ? 0 : 1,
              }}
            />
            <span
              className="block w-5 h-0.5 transition-all duration-300 origin-center"
              style={{
                background: '#4F665F',
                transform: mobileOpen ? 'translateY(-6px) rotate(-45deg)' : 'none',
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile menu drawer */}
      <div
        className="md:hidden overflow-hidden transition-all duration-300"
        style={{
          maxHeight: mobileOpen ? '400px' : '0',
          background: 'rgba(255,255,255,0.98)',
          borderTop: mobileOpen ? '1px solid rgba(212,224,230,0.85)' : 'none',
        }}
      >
        <div className="px-4 py-5 flex flex-col gap-1">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              href={link.href}
              className="text-base font-medium py-3 px-2 rounded-xl transition-colors"
              style={{ color: '#1A3530' }}
              onClick={() => setMobileOpen(false)}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(31,77,63,0.05)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >
              {link.label}
            </Link>
          ))}
          <div className="mt-3 pt-3" style={{ borderTop: '1px solid rgba(212,224,230,0.6)' }}>
            <a
              href="https://vasy.ai/india-avail-free-demo?utm_source=lp&utm_medium=web&utm_campaign=teamvina"
              target="_blank"
              rel="noopener noreferrer"
              className="block text-sm text-center py-3.5 rounded-full font-medium"
              style={{ background: '#1F4D3F', color: '#FFFFFF' }}
              onClick={() => setMobileOpen(false)}
            >
              Schedule a Free Demo Now
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}